import { useCallback, useEffect, useState } from 'react';

const getStorageKey = (clockId: string, itemId: string): string =>
  `sent_to_clock_${clockId}_${itemId}`;

const readMarker = (clockId: string, itemId: string): boolean => {
  try {
    return localStorage.getItem(getStorageKey(clockId, itemId)) === 'true';
  } catch {
    // localStorage unavailable (e.g. private browsing with storage blocked)
    return false;
  }
};

const writeMarker = (clockId: string, itemId: string): void => {
  try {
    localStorage.setItem(getStorageKey(clockId, itemId), 'true');
  } catch {
    // localStorage unavailable (e.g. private browsing with storage blocked)
  }
};

/**
 * Remembers that a klydo was sent to a clock, so the button can stay on
 * "Sent" after a reload.
 *
 * The clock drops a klydo from pendingKlydos as soon as it downloads it, and
 * its feed list rotates, so there is no server-side record to read back. This
 * marker is per browser: another browser, or a factory-reset clock, will offer
 * to send again.
 *
 * Collections don't need this — they stay in the clock's machineCollections,
 * so their button reads live device state instead.
 */
const useSentToClockMarker = (clockId: string | null, itemId?: string) => {
  // Lazy-init so the button is already correct on the first paint, then re-read
  // once the clock claim and the item id have both loaded.
  const [isSent, setIsSent] = useState(() =>
    clockId && itemId ? readMarker(clockId, itemId) : false,
  );

  useEffect(() => {
    setIsSent(clockId && itemId ? readMarker(clockId, itemId) : false);
  }, [clockId, itemId]);

  const markAsSent = useCallback(() => {
    if (!clockId || !itemId) return;
    writeMarker(clockId, itemId);
    setIsSent(true);
  }, [clockId, itemId]);

  return { isSent, markAsSent };
};

export default useSentToClockMarker;
